import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  GroupsService,
  StudentsService,
  ProfileService,
  LocalesService,
  CoursesService,
  CoreQuestionsFactoryService
} from '../../core/services';
import {
  HiddenQuestion,
  EntityQuestion,
  QuestionBase,
  TextboxQuestion,
  DropdownQuestion,
  TagsQuestion,
  DatetimeQuestion,
  QuestionGroup
} from '../../core/components/forms';

@Injectable()
export class LicensesQuestionsService {
  calendarLabels;
  userRole;

  constructor(
    private factory: CoreQuestionsFactoryService,
    private groupsService: GroupsService,
    private studentsService: StudentsService,
    private profileService: ProfileService,
    private coursesService: CoursesService,
    private localesService: LocalesService,
    private translateService: TranslateService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getQuestions(license: any, course: any, client?: any, group?: any) {
    this.calendarLabels = this.translateService.instant('calendar');
    client = license ? { id: license.clientId, name: license.clientName } : client;
    group = license ? { id: license.groupId, name: license.groupName, timezone: license.timezone } : group;
    const student = license
      ? {
          id: license.userId,
          email: license.userEmail,
          name: null,
          surname: null,
          pid: null,
          phone: null
        }
      : null;

    const clientQuestion = this.factory.getClientQuestion({
      client,
      validators: { required: true },
      editable: false,
      order: 1
    });

    const groupQuestion = !license
      ? [
          new HiddenQuestion({
            key: 'groupId',
            value: group.id,
            validators: { required: true },
            order: 101
          })
        ]
      : [
          new EntityQuestion({
            key: 'groupId',
            label: 'nouns.group',
            value: group.id,
            entity: group,
            editable: false,
            dataProvider: this.groupsService,
            order: 2
          })
        ];

    const questions: QuestionBase<any>[] = [
      clientQuestion,
      ...groupQuestion,
      new EntityQuestion({
        key: 'userEmail',
        label: 'nouns.email',
        type: 'email',
        value: student ? student.email : null,
        entity: student,
        entityOptions: {
          valueField: 'email',
          nameField: 'email',
          freeEdit: true,
          listParser: (user) => (user ? `${user.name || ''} ${user.surname || ''} ${user.email}` : ''),
          filters: { clientId: client.id }
        },
        validators: { required: true, mail: true },
        editable: !student,
        dataProvider: this.studentsService,
        order: 3,
        childQuestions: license
          ? []
          : [
              {
                key: 'email', // needed to create user
                value: student ? student.email : null,
                source: 'email'
              },
              {
                key: 'name',
                value: student ? student.name : null,
                source: 'name'
              },
              {
                key: 'surname',
                value: student ? student.surname : null,
                source: 'surname'
              },
              {
                key: 'pid',
                value: student ? student.pid : null,
                source: 'pid'
              },
              {
                key: 'phone',
                value: student ? student.phone : null,
                source: 'phone'
              }
            ]
      }),
      new TextboxQuestion({
        key: 'alias',
        label: 'nouns.alias',
        class: 'col-2-offset',
        value: license ? license.alias : null,
        order: 9
      }),
      this.getStatusQuestion(license),
      this.getTagsQuestion(license),
      new TextboxQuestion({
        key: 'comments',
        label: 'nouns.comments',
        value: license ? license.comments : null,
        order: 10
      })
    ];

    let userQuestions: QuestionBase<any>[] = [];
    if (!license) {
      userQuestions = [
        new TextboxQuestion({
          key: 'name',
          label: 'students.header.name',
          class: 'col-2',
          value: student && student.name ? student.name : null,
          validators: { required: group ? group.hasBonus : false },
          order: 4
        }),
        new TextboxQuestion({
          key: 'surname',
          label: 'nouns.surname',
          class: 'col-2',
          value: student && student.surname ? student.surname : null,
          validators: { required: group ? group.hasBonus : false },
          order: 5
        }),
        new TextboxQuestion({
          key: 'pid',
          label: 'nouns.pid',
          class: 'col-2-offset',
          value: student && student.pid ? student.pid : null,
          validators: { required: group ? group.hasBonus : false },
          order: 6
        }),
        new TextboxQuestion({
          key: 'phone',
          label: 'nouns.phone',
          class: 'col-2-offset',
          value: student && student.phone ? student.phone : null,
          order: 7
        })
      ];
    }
    //There will always be a course here from parameter
    this.localesService.updateArrayCodeIso(course.languages);

    questions.push(...userQuestions);

    questions.push(this.getExtensionsQuestions(license, group.timezone));

    return questions.filter((question) => question !== null).sort((a, b) => a.order - b.order);
  }

  getExtensionsQuestions(license, timezone) {
    let extensions;
    if (license && license.extensions && license.extensions.length) {
      extensions = new QuestionGroup({
        key: 'extensions',
        title: 'nouns.extensions',
        order: 10,
        childQuestions: license.extensions.map(
          (extension, index) =>
            new DatetimeQuestion({
              key: `extensions${index}date`,
              label: this.translateService.instant('licenses.extendLabel', {
                days: extension.days
              }),
              value: extension.date,
              timezone: timezone,
              disabled: true,
              order: index
            })
        )
      });
    }
    return extensions;
  }

  getTagsQuestion(license): QuestionBase<any> {
    return new TagsQuestion({
      key: 'tags',
      label: 'nouns.tags',
      value: license && license.tags ? license.tags : [],
      order: 11
    });
  }

  getStatusQuestion(license): QuestionBase<any> {
    if (!license) {
      return null;
    }
    return new DropdownQuestion({
      key: 'status',
      label: 'nouns.status',
      options: [
        { key: 'created', value: this.translateService.instant('licenses.state.created') },
        { key: 'completed', value: this.translateService.instant('licenses.state.completed') },
        { key: 'cancelled', value: this.translateService.instant('licenses.state.cancelled') },
        { key: 'beforeDemo', value: this.translateService.instant('licenses.state.beforeDemo') },
        { key: 'afterDemo', value: this.translateService.instant('licenses.state.afterDemo') }
      ],
      value: license.status,
      disabled: true,
      order: 9
    });
  }
}
